import { type TablescapeState } from "../state";
import { type DeepPartial } from "ts-essentials";
import merge from "deepmerge";

export const enum TablescapeEvent {
  ADD_BUNDLE_TO_CART = "tbsp:add-bundle-to-cart",
  ADD_CART_LINE = "tbsp:add-cart-line",
  ADD_CART_LINE_COMPLETE = "tbsp:add-cart-line-complete",
  ADD_GIFT_CARD_TO_CART = "tbsp:add-gift-card-to-cart",
  ADD_TO_CART = "tbsp:add-to-cart",
  ADD_TO_CART_ANALYTICS = "tbsp:add-to-cart-analytics",
  CART_ANIMATION_STATE = "tbsp:cart-animation-state",
  CART_ERROR_CLEAR = "tbsp:cart-error-clear",
  CART_LINE_ADD_ERROR = "tbsp:cart-line-add-error",
  CART_LINE_REMOVE_ERROR = "tbsp:cart-line-remove-error",
  CART_LINE_UPDATE_ERROR = "tbsp:cart-line-update-error",
  HIGHLIGHTED_UPSELL_VARIANT_PRICE_UPDATED = "tbsp:highlighted-upsell-variant-price-updated",
  PRODUCT_HERO_VARIANT_SELECTED = "tbsp:product-hero-variant-selected",
  PRODUCT_METADATA_CLEARED = "tbsp:product-metadata-cleared",
  PRODUCT_SELECTOR_VARIANT_SELECTED = "tbsp:product-selector-variant-selected",
  REMOVE_CART_LINE = "tbsp:remove-cart-line",
  REMOVE_CART_LINE_COMPLETE = "tbsp:remove-cart-line-complete",
  REMOVE_FROM_CART = "tbsp:remove-from-cart",
  /** Hydrates `cart.metadata` only; does not enqueue add-to-cart work. */
  RESET_ADD_TO_CART_METADATA = "tbsp:reset-add-to-cart-metadata",
  TOGGLE_CART = "tbsp:toggle-cart",
  TOGGLE_CART_DRAWER = "tbsp:toggle-cart-drawer",
  TOGGLE_CART_LOADING_STATE = "tbsp:toggle-cart-loading-state",
  TOGGLE_GLOBAL_DRAWER = "tbsp:toggle-global-drawer",
  TOGGLE_PAGE_MODAL = "tbsp:toggle-page-modal",
  UPDATE_CART_ATTRIBUTES = "tbsp:update-cart-attributes",
  UPDATE_CART_ID = "tbsp:update-cart-id",
  UPDATE_CART_ITEM_COUNT = "tbsp:update-cart-item-count",
  UPDATE_CART_LINE = "tbsp:update-cart-line",
  UPDATE_CART_LINE_COMPLETE = "tbsp:update-cart-line-complete",
  UPDATE_CART_LINE_PRODUCTS = "tbsp:update-cart-line-products",
  UPDATE_CART_UPSELL_PRODUCTS = "tbsp:update-cart-upsell-products",
  UPDATE_DISCOUNTS = "tbsp:update-discounts",
  UPDATE_GIFT_NOTE_COMPLETE = "tbsp:update-gift-note-complete",
  VARIANT_METADATA_LOADED = "tbsp:variant-metadata-loaded",
  VARIANT_PRICE_UPDATED = "tbsp:variant-price-updated",
  VARIANT_SELECTED = "tbsp:variant-selected",
  VARIANT_UPDATE_STARTED = "tbsp:variant-update-started",
}

export class Context<T> {
  private events = new Map();
  private state: T;

  constructor(initialState: T) {
    this.state = { ...initialState };
  }

  public emit(
    name: string,
    data?: NonNullable<DeepPartial<T>>,
    hydrate = true
  ): void {
    if (hydrate && data) {
      this.hydrate(data);
    }

    this.events
      .get("*")
      ?.forEach((fn: (state: T) => void) => fn(merge(this.state, data ?? {})));

    (this.events.get(name) || new Set()).forEach((fn: (state: T) => void) =>
      fn(
        merge(this.state, data ?? {}, {
          // arrayMerge custom option overwrites the existing array values completely rather than concatenating them
          arrayMerge: (target, source) => source,
        })
      )
    );
  }

  public getSnapshot = (): T => {
    return this.state;
  };

  private hydrate(data: NonNullable<DeepPartial<T>>) {
    this.state = merge(this.state, data, {
      // arrayMerge custom option overwrites the existing array values completely rather than concatenating them
      arrayMerge: (target, source) => source,
    });
  }

  public on(name: string, cb: (data?: T) => void) {
    const eventNamespace = this.events.get(name);
    if (eventNamespace) {
      eventNamespace.add(cb);
    } else {
      this.events.set(name, new Set([cb]));
    }

    return {
      off: (name: string, cb?: (data?: T) => void) => {
        const eventNamespace = this.events.get(name);
        if (cb === undefined) {
          eventNamespace.clear();
        }

        if (eventNamespace.has(cb)) {
          eventNamespace.delete(cb);
        }
      },
    };
  }

  public onAll = (cb: () => void) => {
    const x = this.on("*", cb);

    return () => {
      x.off("*", cb);
    };
  };

  public offAll = (): void => {
    this.events.clear();
  };
}

export const TablescapeContext = new Context<TablescapeState>({
  cart: {
    id: undefined,
    lines: [],
  },
  discounts: [],
  productHero: {
    product: undefined,
    selectedVariant: undefined,
  },
  productSelector: {
    selectedVariantId: undefined,
  },
  products: {},
  selectedOptions: [],
  selectedVariant: undefined,
  ui: {
    cartDrawerAnimating: false,
    cartDrawerOpen: false,
    cartDrawerUserToggled: false,
    cartError: false,
    cartErrorType: null,
    cartErrorMessage: null,
    cartLineAdding: false,
    cartLineUpdating: false,
    cartLineRemoving: false,
    giftNoteDrawerOpen: false,
    giftWithPurchaseDrawerOpen: false,
    productVariantDrawerOpen: false,
    pageModalTarget: null,
    globalDrawerTarget: null,
  },
  videoModal: undefined,
});
